import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";
import { WA_LANGS } from "../utils/whatsappQuickReplies";
import usePermiso from "../hooks/usePermiso";

const emptyFrases = () => ({ one: "", many: "", zero: "" });

function emptyTraducciones(existing) {
  const base = existing && typeof existing === "object" ? existing : {};
  const out = {};
  for (const code of WA_LANGS) {
    const tr = base[code] || {};
    out[code] = {
      label: tr.label || "",
      template: tr.template || "",
      frases: tr.frases
        ? { ...emptyFrases(), ...tr.frases }
        : emptyFrases(),
    };
  }
  return out;
}

export default function PlantillasWhatsApp() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { puede } = usePermiso();
  const verCategorias = puede("pla_wa_categorias", "ver");
  const verIdiomas = puede("pla_wa_idiomas", "ver");
  const modIdiomas = puede("pla_wa_idiomas", "modificar");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [editId, setEditId] = useState(null);
  const [emoji, setEmoji] = useState("💬");
  const [orden, setOrden] = useState(0);
  const [usesCount, setUsesCount] = useState(false);
  const [langTab, setLangTab] = useState("ES");
  const [traducciones, setTraducciones] = useState(emptyTraducciones());
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/whatsapp-plantillas");
      if (data.ok) setItems(data.items || []);
      else setErr(data.error || "Error al cargar");
    } catch (e) {
      setErr(e.response?.data?.error || e.message || "Error de red");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrirEditor = async (id) => {
    setMsg("");
    setErr("");
    setEditId(id);
    try {
      const { data } = await api.get(`/whatsapp-plantillas/item/${encodeURIComponent(id)}`);
      if (!data.ok) {
        setErr(data.error || "Error");
        return;
      }
      const it = data.item || {};
      setEmoji(it.emoji || "💬");
      setOrden(Number(it.orden) || 0);
      setUsesCount(Boolean(it.uses_count));
      setTraducciones(emptyTraducciones(it.traducciones));
      setLangTab("ES");
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  };

  const patchTrad = (code, field, value) => {
    setTraducciones((prev) => ({
      ...prev,
      [code]: { ...prev[code], [field]: value },
    }));
  };

  const patchFrase = (code, key, value) => {
    setTraducciones((prev) => ({
      ...prev,
      [code]: {
        ...prev[code],
        frases: { ...prev[code].frases, [key]: value },
      },
    }));
  };

  const guardar = async () => {
    if (!editId) return;
    setGuardando(true);
    setMsg("");
    setErr("");
    try {
      const payload = {
        emoji,
        orden: Number(orden) || 0,
        uses_count: usesCount,
        frase_variant: null,
        traducciones: Object.fromEntries(
          WA_LANGS.map((code) => {
            const tr = traducciones[code] || {};
            const entry = {
              label: tr.label || "",
              template: tr.template || "",
            };
            if (usesCount) {
              entry.frases = {
                one: tr.frases?.one || "",
                many: tr.frases?.many || "",
                zero: tr.frases?.zero || "",
              };
            }
            return [code, entry];
          }),
        ),
      };
      const { data } = await api.put(
        `/whatsapp-plantillas/item/${encodeURIComponent(editId)}`,
        payload,
      );
      if (!data.ok) {
        setErr(data.error || "No se pudo guardar");
        return;
      }
      setMsg("Guardado. El modal de WhatsApp usará estos textos en el próximo envío.");
      await cargar();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setGuardando(false);
    }
  };

  const tr = traducciones[langTab] || { label: "", template: "", frases: emptyFrases() };

  return (
    <div className="min-h-screen" style={{ background: c.bgApp }}>
      <div
        style={{
          background: c.bgCard,
          borderBottom: `1px solid ${c.border}`,
          padding: "28px 32px 24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: "linear-gradient(135deg,#16a34a,#22c55e)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            💬
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: c.textPrimary }}>
              WhatsApp — respuestas rápidas
            </h1>
            <p style={{ margin: "6px 0 0", fontSize: 13, color: c.textMuted, lineHeight: 1.55 }}>
              Textos de los botones del modal de envío por WhatsApp. Se traducen según el idioma del
              cliente (ficha). Placeholders:{" "}
              <code style={{ fontSize: 12, background: theme.name === "dark" ? "#1e293b" : "#f1f5f9", padding: "2px 6px", borderRadius: 6 }}>
                {"{nombre}"}
              </code>
              ,{" "}
              <code style={{ fontSize: 12, background: theme.name === "dark" ? "#1e293b" : "#f1f5f9", padding: "2px 6px", borderRadius: 6 }}>
                {"{agente}"}
              </code>
              . Si la plantilla usa propiedades adjuntas, incluye{" "}
              <code style={{ fontSize: 12, background: theme.name === "dark" ? "#1e293b" : "#f1f5f9", padding: "2px 6px", borderRadius: 6 }}>
                {"{frase}"}
              </code>{" "}
              y en frases plural usa{" "}
              <code style={{ fontSize: 12, background: theme.name === "dark" ? "#1e293b" : "#f1f5f9", padding: "2px 6px", borderRadius: 6 }}>
                {"{n}"}
              </code>
              . También en{" "}
              <Link to="/plantillas/impresion" style={{ color: c.accentBlue }}>
                Impresión
              </Link>{" "}
              y{" "}
              <Link to="/plantillas/documentacion" style={{ color: c.accentBlue }}>
                Documentación
              </Link>
              .
            </p>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 20px 48px", display: "grid", gap: 24 }}>
        {err && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 10,
              background: "rgba(239,68,68,0.12)",
              border: "1px solid rgba(239,68,68,0.35)",
              color: "#b91c1c",
              fontSize: 14,
            }}
          >
            {err}
          </div>
        )}
        {msg && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 10,
              background: "rgba(16,185,129,0.12)",
              border: "1px solid rgba(16,185,129,0.35)",
              color: "#047857",
              fontSize: 14,
            }}
          >
            {msg}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(260px, 340px) 1fr",
            gap: 20,
            alignItems: "start",
          }}
          className="wa-grid"
        >
          <div
            style={{
              background: c.bgCard,
              border: `1px solid ${c.border}`,
              borderRadius: 14,
              padding: 16,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, color: c.textPrimary, marginBottom: 10 }}>
              Respuestas rápidas
            </div>
            {!verCategorias ? (
              <p style={{ color: c.textMuted, fontSize: 13 }}>
                No tienes permiso para ver las categorías de plantilla.
              </p>
            ) : loading ? (
              <p style={{ color: c.textMuted, fontSize: 13 }}>Cargando…</p>
            ) : (
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {items.map((row) => (
                  <li key={row.id} style={{ marginBottom: 8 }}>
                    <button
                      type="button"
                      onClick={() => abrirEditor(row.id)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "10px 12px",
                        borderRadius: 10,
                        border:
                          editId === row.id
                            ? `2px solid ${c.accentBlue}`
                            : `1px solid ${c.border}`,
                        background: editId === row.id ? c.accentBlueLight : "transparent",
                        color: c.textPrimary,
                        cursor: "pointer",
                        fontSize: 13,
                      }}
                    >
                      <span style={{ marginRight: 8 }}>{row.emoji}</span>
                      <span style={{ fontWeight: 600 }}>{row.label}</span>
                      <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>
                        <code>{row.id}</code>
                        {row.uses_count ? " · plural por nº propiedades" : ""}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div
            style={{
              background: c.bgCard,
              border: `1px solid ${c.border}`,
              borderRadius: 14,
              padding: 18,
            }}
          >
            {!editId ? (
              <p style={{ color: c.textMuted, margin: 0, fontSize: 14 }}>
                Selecciona una respuesta rápida a la izquierda para editarla en todos los idiomas.
              </p>
            ) : (
              <>
                <div style={{ fontSize: 12, color: c.textMuted, marginBottom: 12 }}>
                  Id: <code>{editId}</code>
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                  <label style={{ fontSize: 13, color: c.textPrimary }}>
                    Emoji{" "}
                    <input
                      value={emoji}
                      onChange={(e) => setEmoji(e.target.value)}
                      style={{
                        width: 56,
                        marginLeft: 6,
                        padding: "6px 8px",
                        borderRadius: 8,
                        border: `1px solid ${c.border}`,
                        background: c.bgApp,
                        color: c.textPrimary,
                      }}
                    />
                  </label>
                  <label style={{ fontSize: 13, color: c.textPrimary }}>
                    Orden{" "}
                    <input
                      type="number"
                      value={orden}
                      onChange={(e) => setOrden(e.target.value)}
                      style={{
                        width: 64,
                        marginLeft: 6,
                        padding: "6px 8px",
                        borderRadius: 8,
                        border: `1px solid ${c.border}`,
                        background: c.bgApp,
                        color: c.textPrimary,
                      }}
                    />
                  </label>
                  <label
                    style={{
                      fontSize: 13,
                      color: c.textMuted,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={usesCount}
                      disabled
                      readOnly
                    />
                    Ajusta texto según nº de propiedades adjuntas
                  </label>
                </div>

                {verIdiomas ? (
                <>
                <div style={{ opacity: modIdiomas ? 1 : 0.6, pointerEvents: modIdiomas ? "auto" : "none" }}
                  title={!modIdiomas ? "No tienes permiso para modificar los idiomas de la plantilla" : undefined}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                  {WA_LANGS.map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setLangTab(code)}
                      style={{
                        padding: "6px 12px",
                        borderRadius: 8,
                        border: langTab === code ? `2px solid ${c.accentBlue}` : `1px solid ${c.border}`,
                        background: langTab === code ? c.accentBlueLight : "transparent",
                        color: c.textPrimary,
                        fontWeight: langTab === code ? 700 : 500,
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                    >
                      {code}
                    </button>
                  ))}
                </div>

                <label style={{ display: "block", fontWeight: 600, fontSize: 13, color: c.textPrimary, marginBottom: 6 }}>
                  Título del botón ({langTab})
                </label>
                <input
                  value={tr.label}
                  onChange={(e) => patchTrad(langTab, "label", e.target.value)}
                  style={{
                    width: "100%",
                    marginBottom: 14,
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: `1px solid ${c.border}`,
                    background: c.bgApp,
                    color: c.textPrimary,
                    fontSize: 14,
                    boxSizing: "border-box",
                  }}
                />

                <label style={{ display: "block", fontWeight: 600, fontSize: 13, color: c.textPrimary, marginBottom: 6 }}>
                  Mensaje ({langTab})
                </label>
                <textarea
                  value={tr.template}
                  onChange={(e) => patchTrad(langTab, "template", e.target.value)}
                  rows={usesCount ? 5 : 8}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "12px 14px",
                    borderRadius: 8,
                    border: `1px solid ${c.border}`,
                    background: c.bgApp,
                    color: c.textPrimary,
                    fontSize: 14,
                    lineHeight: 1.45,
                    resize: "vertical",
                  }}
                />

                {usesCount && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: c.textPrimary, marginBottom: 8 }}>
                      Frases según propiedades ({langTab}) — sustituyen {"{frase}"}
                    </div>
                    {[
                      ["one", "1 propiedad"],
                      ["many", "Varias ({n} en el texto)"],
                      ["zero", "0 propiedades"],
                    ].map(([key, hint]) => (
                      <div key={key} style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 12, color: c.textMuted, marginBottom: 4 }}>{hint}</div>
                        <input
                          value={tr.frases?.[key] || ""}
                          onChange={(e) => patchFrase(langTab, key, e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            borderRadius: 8,
                            border: `1px solid ${c.border}`,
                            background: c.bgApp,
                            color: c.textPrimary,
                            fontSize: 13,
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
                </div>

                <div style={{ marginTop: 14 }}>
                  <button
                    type="button"
                    disabled={guardando || !modIdiomas}
                    title={!modIdiomas ? "No tienes permiso para modificar los idiomas de la plantilla" : undefined}
                    onClick={guardar}
                    style={{
                      padding: "10px 20px",
                      borderRadius: 10,
                      border: "none",
                      background: "#16a34a",
                      color: "#fff",
                      fontWeight: 700,
                      cursor: guardando || !modIdiomas ? "not-allowed" : "pointer",
                      opacity: guardando || !modIdiomas ? 0.6 : 1,
                    }}
                  >
                    {guardando ? "Guardando…" : "Guardar"}
                  </button>
                </div>
                </>
                ) : (
                  <p style={{ color: c.textMuted, margin: 0, fontSize: 13 }}>
                    No tienes permiso para ver los idiomas de la plantilla.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 820px) {
          .wa-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
